import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { UI_LOCALE_CODES, isRtlLocale, type UiLocale } from '@/shared/localization-contracts';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import zh from './locales/zh.json';
import ar from './locales/ar.json';
import th from './locales/th.json';
// Phase 2 Sprint 6/J6.2 — 13 new locales (LD6). Seeded as English fallback;
// admin review pipeline flips locale_metadata.is_public when a locale is
// ready for learners.
import de from './locales/de.json';
import pt from './locales/pt.json';
import ko from './locales/ko.json';
import vi from './locales/vi.json';
import tr from './locales/tr.json';
import pl from './locales/pl.json';
import nl from './locales/nl.json';
import hi from './locales/hi.json';
import el from './locales/el.json';
import id from './locales/id.json';
import he from './locales/he.json';
import sw from './locales/sw.json';
import tl from './locales/tl.json';

export type { UiLocale } from '@/shared/localization-contracts';
/** @deprecated Use UiLocale for UI selection and ContentLocale for Event Pack output. */
export type Locale = UiLocale;

/** Locales bundled with the client. The list of `isPublic` locales is fetched
 * from `/api/i18n/locales` at runtime so admins can flip new locales on
 * without a deploy. SUPPORTED_LOCALES here is the bundled superset. */
export const SUPPORTED_LOCALES: UiLocale[] = [...UI_LOCALE_CODES];
/** Locales shipped publicly without admin opt-in. Settings starts here and is
 * augmented at runtime with admin-reviewed locales from the metadata API. */
export const PUBLIC_LOCALES_BOOTSTRAP: UiLocale[] = [
  'en',
  'es',
  'fr',
  'it',
  'ja',
  'zh',
  'ar',
  'th',
];
export const DEFAULT_LOCALE: UiLocale = 'en';
export const RTL_LOCALES = new Set<string>(['ar', 'he', 'fa', 'ur']);

export const LOCALES_META: Record<
  UiLocale,
  { native: string; flag: string; dir: 'ltr' | 'rtl'; font: string | null }
> = {
  en: { native: 'English', flag: 'EN', dir: 'ltr', font: null },
  es: { native: 'Español', flag: 'ES', dir: 'ltr', font: null },
  fr: { native: 'Français', flag: 'FR', dir: 'ltr', font: null },
  it: { native: 'Italiano', flag: 'IT', dir: 'ltr', font: null },
  ja: { native: '日本語', flag: 'JA', dir: 'ltr', font: '"Noto Sans JP"' },
  zh: { native: '中文', flag: 'ZH', dir: 'ltr', font: '"Noto Sans SC"' },
  ar: { native: 'العربية', flag: 'AR', dir: 'rtl', font: '"Noto Sans Arabic"' },
  th: { native: 'ไทย', flag: 'TH', dir: 'ltr', font: '"Noto Sans Thai"' },
  de: { native: 'Deutsch', flag: 'DE', dir: 'ltr', font: null },
  pt: { native: 'Português', flag: 'PT', dir: 'ltr', font: null },
  ko: { native: '한국어', flag: 'KO', dir: 'ltr', font: '"Noto Sans KR"' },
  vi: { native: 'Tiếng Việt', flag: 'VI', dir: 'ltr', font: null },
  tr: { native: 'Türkçe', flag: 'TR', dir: 'ltr', font: null },
  pl: { native: 'Polski', flag: 'PL', dir: 'ltr', font: null },
  nl: { native: 'Nederlands', flag: 'NL', dir: 'ltr', font: null },
  hi: { native: 'हिन्दी', flag: 'HI', dir: 'ltr', font: '"Noto Sans Devanagari"' },
  el: { native: 'Ελληνικά', flag: 'EL', dir: 'ltr', font: null },
  id: { native: 'Bahasa Indonesia', flag: 'ID', dir: 'ltr', font: null },
  he: { native: 'עברית', flag: 'HE', dir: 'rtl', font: '"Noto Sans Hebrew"' },
  sw: { native: 'Kiswahili', flag: 'SW', dir: 'ltr', font: null },
  tl: { native: 'Tagalog', flag: 'TL', dir: 'ltr', font: null },
};

export function getAvailableLocales(): UiLocale[] {
  return SUPPORTED_LOCALES;
}

export function isRTL(locale: string): boolean {
  return isRtlLocale(locale);
}

export function getDirection(locale: string): 'ltr' | 'rtl' {
  return isRTL(locale) ? 'rtl' : 'ltr';
}

const BUNDLED_RESOURCES = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  it: { translation: it },
  ja: { translation: ja },
  zh: { translation: zh },
  ar: { translation: ar },
  th: { translation: th },
  de: { translation: de },
  pt: { translation: pt },
  ko: { translation: ko },
  vi: { translation: vi },
  tr: { translation: tr },
  pl: { translation: pl },
  nl: { translation: nl },
  hi: { translation: hi },
  el: { translation: el },
  id: { translation: id },
  he: { translation: he },
  sw: { translation: sw },
  tl: { translation: tl },
} as const;

function collectStringKeys(value: unknown, prefix = '', output = new Set<string>()): Set<string> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      collectStringKeys(nested, prefix ? `${prefix}.${key}` : key, output);
    }
  } else if (typeof value === 'string') {
    output.add(prefix);
  }
  return output;
}

export function validateRemoteLocalePayload(locale: string, payload: unknown): boolean {
  if (!(locale in BUNDLED_RESOURCES) || !payload || typeof payload !== 'object') return false;
  const bundled = BUNDLED_RESOURCES[locale as UiLocale].translation;
  const expected = [...collectStringKeys(bundled)].sort();
  const actual = [...collectStringKeys(payload)].sort();
  return expected.length === actual.length && expected.every((key, index) => key === actual[index]);
}

const enableRemoteBackend = import.meta.env.PROD || import.meta.env.VITE_I18N_REMOTE === 'true';

if (enableRemoteBackend) {
  i18n.use(Backend);
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    resources: BUNDLED_RESOURCES,
    partialBundledLanguages: true,
    keySeparator: '.',
    returnNull: false,
    returnEmptyString: false,
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
    backend: enableRemoteBackend
      ? {
          // Locale payloads are served by the Worker from the D1 `translations` table.
          loadPath: '/api/i18n/{{lng}}',
          requestOptions: {
            cache: 'default',
          },
          parse(data: string, languages?: string | string[]) {
            const payload = JSON.parse(data) as unknown;
            const locale = Array.isArray(languages) ? languages[0] : languages;
            if (locale && !validateRemoteLocalePayload(locale, payload)) {
              if (import.meta.env.DEV) {
                console.warn('i18n.remote_payload_contract_mismatch', { locale });
              }
              return BUNDLED_RESOURCES[locale as UiLocale]?.translation ?? en;
            }
            return payload as Record<string, unknown>;
          },
        }
      : undefined,
  });

export const t = i18n.t.bind(i18n);

// Existing app surfaces use useT(); preserved verbatim.
export const useT = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  return {
    locale: i18nInstance.language as UiLocale,
    setLocale: (lng: UiLocale) => i18nInstance.changeLanguage(lng),
    t,
    direction: getDirection(i18nInstance.language),
  };
};

// Marketing blocks read whole subtrees of nested copy, like the prototype's
// `L.hero.default.line1` pattern. useCopy returns a getter that uses
// i18next's returnObjects option under the hood.
export const useCopy = () => {
  const { t } = useTranslation();
  return <T = unknown>(key: string): T => t(key, { returnObjects: true }) as unknown as T;
};

export default i18n;
